import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {CreateProjectConfirmationFormSchema} from '@/features/projects/formSchemas'
import {useWorkingDays} from '@/features/projects/hooks/useWorkingDays'
import {useStoreProject} from '@/features/projects/queries/useStoreProject'
import {assistiveRegimeToRehabCareCommitment} from '@/features/projects/utils'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import {zodResolver} from '@hookform/resolvers/zod'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {generatePath, useNavigate} from 'react-router-dom'

export const CreateProjectConfirmationDialog = ({
    dialogStore,
    defaultValues
}: {
    dialogStore: DialogStore
    defaultValues: CreateProjectConfirmationFormSchema
}) => {
    const form = useForm<CreateProjectConfirmationFormSchema>({
        resolver: zodResolver(CreateProjectConfirmationFormSchema),
        defaultValues
    })

    const patient = useWatch({control: form.control, name: 'patient'})
    const startDate = useWatch({control: form.control, name: 'startDate'})
    const expirationDate = useWatch({control: form.control, name: 'expirationDate'})
    const durationDays = useWatch({control: form.control, name: 'durationDays'})

    const {t} = useTranslation()
    const navigate = useNavigate()

    const actualDays = useWorkingDays({
        startDate: startDate ? dayjs(startDate).startOf('day').toDate() : null,
        expirationDate: expirationDate ? dayjs(expirationDate).endOf('day').toDate() : null,
        durationDays: durationDays ?? undefined
    })

    const {mutate: createProject, isPending} = useStoreProject({
        onSuccess: async project => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            toast.success(
                <Flexbox direction="column">
                    <Text size="textSm" weight="500">
                        {t('projects:project_created')}
                    </Text>
                    {project.patient.fullName && (
                        <Text size="textSm" palette="neutral" shade="500">
                            {project.patient.fullName}
                        </Text>
                    )}
                </Flexbox>
            )

            navigate(generatePath(routes.PROJECT_DETAILS_ROUTE.handle.path, {id: project.id}), {
                replace: true
            })
        },
        onError: error => {
            console.error('Create project error: ', error)
            dialogStore.setOpen(false)
        }
    })

    const handleConfirmSubmit = form.handleSubmit(formValues => {
        createProject({
            payload: {
                patientId: formValues.patient.id,
                treatmentRequestId: formValues.treatmentRequestId || undefined,
                type: formValues.projectType,
                sector: formValues.sector,
                status: 'opening',
                numberOfCycles: formValues.projectType == 'cyclic' ? formValues.numberOfCycles : undefined,
                assistiveRegime: formValues.assistiveRegime ?? null, // This is a workaround to avoid the required error from the backend
                durationDays: formValues.durationDays || undefined,
                startDate: formatLocaleDate(formValues.startDate, 'YYYY-MM-DD'),
                expirationDate: formValues.expirationDate
                    ? formatLocaleDate(formValues.expirationDate, 'YYYY-MM-DD')
                    : undefined,
                actualDays: actualDays || undefined,
                specialistId: formValues.specialist?.id,
                sender: formValues.sender ?? undefined,
                notificationRecipientIds:
                    formValues.notificationsRecipients?.map(recipient => recipient.id) ?? undefined,
                notificationDetails: formValues.notificationDetails ?? undefined,
                // This is sent to the backend just to allow the SIAR export, it is not a field of our form
                rehabilitativeAssistanceCommitment:
                    formValues.sector == 'outpatient' && formValues.assistiveRegime
                        ? assistiveRegimeToRehabCareCommitment[formValues.assistiveRegime].toString()
                        : undefined
            }
        })
    })

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column">
                        <DialogHeading>{t('projects:add_project_confirmation_modal_title')}</DialogHeading>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:patient')}: {patient.fullName}
                        </Text>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
